/*
 * nco-collection-filters.js
 *
 * Client-side filtering engine for collection pages — variant-aware,
 * with live counts. Reads the JSON product index rendered by
 * snippets/nco-collection-filters.liquid and performs all filtering
 * in the browser. Emits nco:filter:change custom events for the
 * collection grid to consume.
 *
 * Architecture:
 *   - AND logic between filter groups
 *   - OR logic within a filter group
 *   - Matrix-based matching: products are visible only if a real
 *     variant combo satisfies all active option filters simultaneously
 *   - Cross-filter live counts
 *   - Zero-count options disabled to prevent dead ends
 *   - URL state sync via history.replaceState
 */

const SELECTORS = {
  container: '[data-nco-filters]',
  index: '#nco-product-index',
  group: '[data-nco-filter-group]',
  groupHeader: '.nco-filters__group-header',
  options: '[data-nco-filter-options]',
  option: '[data-nco-filter-value]',
  chips: '[data-nco-filter-chips]',
  clearAll: '[data-nco-filter-clear-all]',
  chip: '[data-nco-chip]',
  chipRemove: '[data-nco-chip-remove]',
};

const STATE = {
  active: 'nco-filters__option--active',
  disabled: 'nco-filters__option--disabled',
  collapsed: 'nco-filters__group--collapsed',
};

/* ------------------------------------------------------------------ */
/* Band definitions for range filters                                  */
/* ------------------------------------------------------------------ */

const WIDTH_BANDS = [
  { label: 'Under 400mm', min: 0, max: 399, key: 'under-400' },
  { label: '400–600mm', min: 400, max: 600, key: '400-600' },
  { label: '600–800mm', min: 601, max: 800, key: '600-800' },
  { label: '800mm+', min: 801, max: Infinity, key: '800-plus' },
];

const DEPTH_BANDS = [
  { label: 'Under 300mm', min: 0, max: 299, key: 'under-300' },
  { label: '300–400mm', min: 300, max: 400, key: '300-400' },
  { label: '400–500mm', min: 401, max: 500, key: '400-500' },
  { label: '500mm+', min: 501, max: Infinity, key: '500-plus' },
];

const PRICE_BANDS = [
  { label: 'Under $1,000', min: 0, max: 999, key: 'under-1000' },
  { label: '$1,000–$1,500', min: 1000, max: 1500, key: '1000-1500' },
  { label: '$1,500–$3,000', min: 1501, max: 3000, key: '1500-3000' },
  { label: '$3,000+', min: 3001, max: Infinity, key: '3000-plus' },
];

const URL_PARAM_ORDER = [
  'colour', 'mount', 'size', 'finish', 'tray',
  'overflow', 'tapHole', 'overflowFinish',
  'widthBand', 'depthBand', 'priceBand', 'shapeStyle', 'roomType'
];

const FILTER_TO_URL_KEY = {
  colour: 'colour',
  mountType: 'mount',
  overflow: 'overflow',
  tapHole: 'tapHole',
  tray: 'tray',
  shapeStyle: 'shapeStyle',
  widthBand: 'widthBand',
  depthBand: 'depthBand',
  priceBand: 'priceBand',
  roomType: 'roomType',
};

const URL_KEY_TO_FILTER = {};
Object.keys(FILTER_TO_URL_KEY).forEach(function (k) {
  URL_KEY_TO_FILTER[FILTER_TO_URL_KEY[k]] = k;
});

/* ------------------------------------------------------------------ */
/* Product index & filter state                                        */
/* ------------------------------------------------------------------ */

let products = [];
const activeFilters = {};
let allOptions = {};

/* ------------------------------------------------------------------ */
/* Utility                                                             */
/* ------------------------------------------------------------------ */

function handleize(s) {
  return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function escapeHTML(str) {
  const div = document.createElement('div');
  div.appendChild(document.createTextNode(str));
  return div.innerHTML;
}

function escapeAttr(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function formatPrice(cents) {
  return '$' + (cents / 100).toLocaleString('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/* ------------------------------------------------------------------ */
/* Matrix-based combo matching                                         */
/* ------------------------------------------------------------------ */

function comboMatchesFilters(combo, product, filters) {
  var fm = product.filterMatrix;

  var colourValues = filters.colour;
  if (colourValues && colourValues.length > 0) {
    var comboColours = combo.availableColours || fm.colours || [];
    var hasColourMatch = colourValues.some(function (v) {
      return comboColours.indexOf(handleize(v)) !== -1;
    });
    if (!hasColourMatch) return false;
  }

  var mountValues = filters.mountType;
  if (mountValues && mountValues.length > 0) {
    var comboMount = combo.optionParams.mount;
    if (!comboMount) return false;
    if (mountValues.indexOf(comboMount) === -1) return false;
  }

  var overflowValues = filters.overflow;
  if (overflowValues && overflowValues.length > 0) {
    var comboOverflow = combo.optionParams.overflow;
    if (!comboOverflow) return false;
    if (overflowValues.indexOf(comboOverflow) === -1) return false;
  }

  var tapHoleValues = filters.tapHole;
  if (tapHoleValues && tapHoleValues.length > 0) {
    var comboTapHole = combo.optionParams.tapHole;
    if (!comboTapHole) return false;
    var mapped = tapHoleValues.map(function (v) {
      return v === 'Single Tap Hole' ? '1 Tap Hole' : v;
    });
    if (mapped.indexOf(comboTapHole) === -1) return false;
  }

  var trayValues = filters.tray;
  if (trayValues && trayValues.length > 0) {
    var comboTray = combo.optionParams.tray;
    if (!comboTray) return false;
    if (trayValues.indexOf(comboTray) === -1) return false;
  }

  var priceValues = filters.priceBand;
  if (priceValues && priceValues.length > 0) {
    var comboPrice = combo.priceMin / 100;
    var priceMatch = priceValues.some(function (bandKey) {
      var band = PRICE_BANDS.find(function (b) { return b.key === bandKey; });
      return band && comboPrice >= band.min && comboPrice <= band.max;
    });
    if (!priceMatch) return false;
  }

  return true;
}

function getMatchingCombos(product, filters) {
  var fm = product.filterMatrix;
  if (!fm || !fm.combos) return null;
  var combos = Object.values(fm.combos);
  return combos.filter(function (c) {
    return comboMatchesFilters(c, product, filters);
  });
}

/* ------------------------------------------------------------------ */
/* Matching logic                                                      */
/* ------------------------------------------------------------------ */

var MATRIX_GROUPS = ['colour', 'mountType', 'overflow', 'tapHole', 'tray', 'priceBand'];
var BOOLEAN_GROUPS = ['shapeStyle', 'widthBand', 'depthBand', 'roomType'];
var SINGLE_SELECT_GROUPS = { tapHole: true, overflow: true, tray: true };

function productMatchesGroup(product, groupKey, values) {
  if (!values || values.length === 0) return true;

  switch (groupKey) {
    case 'colour':
      return values.some(function (v) {
        return (product.colours || []).some(function (c) {
          return (typeof c === 'string' ? c : c.name) === v;
        });
      });

    case 'mountType':
      return values.some(function (v) {
        return (product.mountTypes || []).indexOf(v) !== -1;
      });

    case 'shapeStyle':
      return values.some(function (v) {
        return (product.shapes || []).indexOf(v) !== -1 || (product.styles || []).indexOf(v) !== -1;
      });

    case 'widthBand':
      return values.some(function (bandKey) {
        var band = WIDTH_BANDS.find(function (b) { return b.key === bandKey; });
        if (!band) return false;
        return (product.widths || []).some(function (w) { return w >= band.min && w <= band.max; });
      });

    case 'depthBand':
      return values.some(function (bandKey) {
        var band = DEPTH_BANDS.find(function (b) { return b.key === bandKey; });
        if (!band) return false;
        return (product.depths || []).some(function (d) { return d >= band.min && d <= band.max; });
      });

    case 'priceBand':
      return values.some(function (bandKey) {
        var band = PRICE_BANDS.find(function (b) { return b.key === bandKey; });
        if (!band) return false;
        return (product.prices || []).some(function (p) { return p >= band.min && p <= band.max; });
      });

    case 'overflow':
      return values.some(function (v) {
        return (product.overflow || []).indexOf(v) !== -1;
      });

    case 'tapHole':
      return values.some(function (v) {
        return (product.tapHole || []).indexOf(v) !== -1;
      });

    case 'tray':
      return values.some(function (v) {
        return (product.tray || []).indexOf(v) !== -1;
      });

    case 'roomType':
      return values.some(function (v) {
        return (product.roomTypes || []).indexOf(v) !== -1;
      });

    default:
      return true;
  }
}

function hasActiveMatrixFilters(filters) {
  for (var i = 0; i < MATRIX_GROUPS.length; i++) {
    var key = MATRIX_GROUPS[i];
    if (filters[key] && filters[key].length > 0) return true;
  }
  return false;
}

function productMatchesAllFilters(product, filters) {
  // Boolean-only groups: always use simple matching
  for (var i = 0; i < BOOLEAN_GROUPS.length; i++) {
    var groupKey = BOOLEAN_GROUPS[i];
    var values = filters[groupKey];
    if (values && values.length > 0 && !productMatchesGroup(product, groupKey, values)) {
      return false;
    }
  }

  // Matrix-aware groups
  if (!hasActiveMatrixFilters(filters)) return true;

  var fm = product.filterMatrix;
  if (!fm) {
    // No matrix: fall back to boolean matching for matrix groups
    for (var j = 0; j < MATRIX_GROUPS.length; j++) {
      var gk = MATRIX_GROUPS[j];
      var vals = filters[gk];
      if (vals && vals.length > 0 && !productMatchesGroup(product, gk, vals)) {
        return false;
      }
    }
    return true;
  }

  // Has matrix: at least one combo must satisfy all active matrix-group constraints
  var matching = getMatchingCombos(product, filters);
  return matching && matching.length > 0;
}

function getMatchingProducts(filters) {
  return products.filter(function (p) {
    return productMatchesAllFilters(p, filters);
  });
}

function getVisibleIds() {
  return getMatchingProducts(activeFilters).map(function (p) { return p.id; });
}

/* ------------------------------------------------------------------ */
/* Variant-aware price/URL resolution                                  */
/* ------------------------------------------------------------------ */

function resolveFilteredVariant(product, filters) {
  var result = {
    price: product.priceMin || 0,
    url: '/products/' + product.handle,
    isRange: true,
  };

  var fm = product.filterMatrix;
  if (!fm || !hasActiveMatrixFilters(filters)) return result;

  var matching = getMatchingCombos(product, filters);
  if (!matching || matching.length === 0) return result;

  var prices = [];
  for (var i = 0; i < matching.length; i++) {
    prices.push(matching[i].priceMin);
    if (matching[i].priceMax !== matching[i].priceMin) {
      prices.push(matching[i].priceMax);
    }
  }

  var minPrice = Math.min.apply(null, prices);
  var maxPrice = Math.max.apply(null, prices);
  result.price = minPrice;
  result.isRange = minPrice !== maxPrice;

  // Build URL with option params (single-value filters only)
  var params = [];
  var colourVals = filters.colour;
  if (colourVals && colourVals.length === 1) {
    params.push('colour=' + encodeURIComponent(colourVals[0]));
  }
  var mountVals = filters.mountType;
  if (mountVals && mountVals.length === 1 && mountVals[0] !== 'Freestanding') {
    params.push('mount=' + encodeURIComponent(mountVals[0]));
  }
  var overflowVals = filters.overflow;
  if (overflowVals && overflowVals.length === 1) {
    params.push('overflow=' + encodeURIComponent(overflowVals[0]));
  }
  var tapHoleVals = filters.tapHole;
  if (tapHoleVals && tapHoleVals.length === 1) {
    var thVal = tapHoleVals[0];
    if (thVal === 'Single Tap Hole' || thVal === '1 Tap Hole') thVal = 'Yes';
    else if (thVal === 'No Tap Hole') thVal = 'No';
    params.push('tapHole=' + encodeURIComponent(thVal));
  }
  var trayVals = filters.tray;
  if (trayVals && trayVals.length === 1) {
    params.push('tray=' + encodeURIComponent(trayVals[0]));
  }

  if (params.length > 0) {
    result.url = '/products/' + product.handle + '?' + params.join('&');
  }

  return result;
}

/* ------------------------------------------------------------------ */
/* Cross-filter count calculation                                      */
/* ------------------------------------------------------------------ */

function calculateCounts() {
  var counts = {};
  var groupKeys = Object.keys(allOptions);

  for (var g = 0; g < groupKeys.length; g++) {
    var groupKey = groupKeys[g];
    var options = allOptions[groupKey];
    counts[groupKey] = {};

    var otherFilters = {};
    var filterKeys = Object.keys(activeFilters);
    for (var f = 0; f < filterKeys.length; f++) {
      if (filterKeys[f] !== groupKey) {
        otherFilters[filterKeys[f]] = activeFilters[filterKeys[f]];
      }
    }

    var baseMatches = getMatchingProducts(otherFilters);

    for (var o = 0; o < options.length; o++) {
      var optionValue = options[o].value;
      var testFilters = {};
      Object.keys(otherFilters).forEach(function (k) {
        testFilters[k] = otherFilters[k];
      });
      testFilters[groupKey] = [optionValue];

      var count = 0;
      for (var m = 0; m < baseMatches.length; m++) {
        if (productMatchesAllFilters(baseMatches[m], testFilters)) {
          count++;
        }
      }
      counts[groupKey][optionValue] = count;
    }
  }
  return counts;
}

/* ------------------------------------------------------------------ */
/* Option extraction from product index                                */
/* ------------------------------------------------------------------ */

function extractUniqueValues(arr, accessor) {
  var seen = {};
  var result = [];
  for (var i = 0; i < arr.length; i++) {
    var vals = accessor(arr[i]);
    if (!vals) continue;
    if (!Array.isArray(vals)) vals = [vals];
    for (var j = 0; j < vals.length; j++) {
      var v = vals[j];
      if (v !== null && v !== undefined && v !== '' && !seen[v]) {
        seen[v] = true;
        result.push(v);
      }
    }
  }
  return result.sort();
}

function buildAllOptions() {
  allOptions = {};

  // Colour: extracted from product index colours array (names)
  allOptions.colour = extractUniqueValues(products, function (p) {
    return (p.colours || []).map(function (c) {
      return typeof c === 'string' ? c : c.name;
    });
  }).map(function (v) { return { value: v, label: v }; });

  allOptions.mountType = extractUniqueValues(products, function (p) {
    return p.mountTypes;
  }).map(function (v) { return { value: v, label: v }; });

  var shapesAndStyles = [];
  var ssSeen = {};
  for (var i = 0; i < products.length; i++) {
    var shapes = products[i].shapes || [];
    var styles = products[i].styles || [];
    var combined = shapes.concat(styles);
    for (var j = 0; j < combined.length; j++) {
      if (!ssSeen[combined[j]]) {
        ssSeen[combined[j]] = true;
        shapesAndStyles.push(combined[j]);
      }
    }
  }
  shapesAndStyles.sort();
  allOptions.shapeStyle = shapesAndStyles.map(function (v) {
    return { value: v, label: v };
  });

  allOptions.widthBand = WIDTH_BANDS.map(function (b) {
    return { value: b.key, label: b.label };
  });

  allOptions.depthBand = DEPTH_BANDS.map(function (b) {
    return { value: b.key, label: b.label };
  });

  allOptions.priceBand = PRICE_BANDS.map(function (b) {
    return { value: b.key, label: b.label };
  });

  allOptions.overflow = extractUniqueValues(products, function (p) {
    return p.overflow;
  }).map(function (v) { return { value: v, label: v }; });

  allOptions.tapHole = extractUniqueValues(products, function (p) {
    return p.tapHole;
  }).map(function (v) { return { value: v, label: v }; });

  allOptions.tray = extractUniqueValues(products, function (p) {
    return p.tray;
  }).map(function (v) { return { value: v, label: v }; });

  allOptions.roomType = extractUniqueValues(products, function (p) {
    return p.roomTypes;
  }).map(function (v) { return { value: v, label: v }; });
}

/* ------------------------------------------------------------------ */
/* DOM rendering                                                       */
/* ------------------------------------------------------------------ */

const GROUP_LABELS = {
  colour: 'Colour',
  mountType: 'Mount Type',
  shapeStyle: 'Shape & Style',
  widthBand: 'Width',
  depthBand: 'Depth',
  priceBand: 'Price',
  overflow: 'Overflow',
  tapHole: 'Tap Hole',
  tray: 'Tray',
  roomType: 'Room',
};

function renderOptionHTML(groupKey, option) {
  var isCheckboxGroup = groupKey === 'shapeStyle' || groupKey === 'roomType';

  if (isCheckboxGroup) {
    var cbId = 'nco-filter-' + groupKey + '-' + escapeAttr(option.value).replace(/\s+/g, '-').toLowerCase();
    return '<label class="nco-filters__option nco-filters__option--checkbox" data-nco-filter-value="' + escapeAttr(option.value) + '">'
      + '<input type="checkbox" class="nco-filters__checkbox-input" id="' + cbId + '" aria-label="' + escapeAttr(option.label) + '">'
      + '<span class="nco-filters__checkbox-mark" aria-hidden="true"></span>'
      + '<span class="nco-filters__option-label">' + escapeHTML(option.label) + '</span>'
      + '<span class="nco-filters__count"></span>'
      + '</label>';
  }

  return '<button type="button" class="nco-filters__option nco-filters__option--button"'
    + ' data-nco-filter-value="' + escapeAttr(option.value) + '"'
    + ' aria-pressed="false">'
    + '<span class="nco-filters__option-label">' + escapeHTML(option.label) + '</span>'
    + '<span class="nco-filters__count"></span>'
    + '</button>';
}

function renderGroupOptions(groupKey) {
  // Colour options are rendered in Liquid (with swatch dots), not here
  if (groupKey === 'colour') return;

  var container = document.querySelector('[data-nco-filter-options="' + groupKey + '"]');
  if (!container) return;

  var options = allOptions[groupKey];
  if (!options || options.length === 0) {
    var group = container.closest(SELECTORS.group);
    if (group) group.hidden = true;
    return;
  }

  var html = '';
  for (var i = 0; i < options.length; i++) {
    html += renderOptionHTML(groupKey, options[i]);
  }
  container.innerHTML = html;
}

function renderAllOptions() {
  var groups = Object.keys(allOptions);
  for (var i = 0; i < groups.length; i++) {
    renderGroupOptions(groups[i]);
  }
}

function updateCountDisplay(counts) {
  var groupKeys = Object.keys(counts);
  for (var g = 0; g < groupKeys.length; g++) {
    var groupKey = groupKeys[g];
    var groupCounts = counts[groupKey];
    var optionValues = Object.keys(groupCounts);
    var groupContainer = document.querySelector('[data-nco-filter-options="' + groupKey + '"]');
    if (!groupContainer) continue;

    for (var o = 0; o < optionValues.length; o++) {
      var value = optionValues[o];
      var count = groupCounts[value];

      var optionEl = groupContainer.querySelector('[data-nco-filter-value="' + CSS.escape(value) + '"]');
      if (!optionEl) continue;

      var countEl = optionEl.querySelector('.nco-filters__count');
      if (countEl) {
        countEl.textContent = '(' + count + ')';
      }

      var isActive = activeFilters[groupKey] && activeFilters[groupKey].indexOf(value) !== -1;
      if (count === 0 && !isActive) {
        optionEl.classList.add(STATE.disabled);
        optionEl.setAttribute('aria-disabled', 'true');
        if (optionEl.tagName === 'BUTTON') {
          optionEl.disabled = true;
        }
        var cbInput = optionEl.querySelector('.nco-filters__checkbox-input');
        if (cbInput) cbInput.disabled = true;
      } else {
        optionEl.classList.remove(STATE.disabled);
        optionEl.removeAttribute('aria-disabled');
        if (optionEl.tagName === 'BUTTON') {
          optionEl.disabled = false;
        }
        var cbInput2 = optionEl.querySelector('.nco-filters__checkbox-input');
        if (cbInput2) cbInput2.disabled = false;
      }
    }
  }
}

/* ------------------------------------------------------------------ */
/* Chip management                                                     */
/* ------------------------------------------------------------------ */

function renderChips() {
  var chipsContainer = document.querySelector(SELECTORS.chips);
  if (!chipsContainer) return;

  var clearBtn = chipsContainer.querySelector(SELECTORS.clearAll);
  var existingChips = chipsContainer.querySelectorAll(SELECTORS.chip);
  for (var i = existingChips.length - 1; i >= 0; i--) {
    existingChips[i].remove();
  }

  var hasActive = false;
  var groupKeys = Object.keys(activeFilters);

  for (var g = 0; g < groupKeys.length; g++) {
    var groupKey = groupKeys[g];
    var values = activeFilters[groupKey];
    if (!values || values.length === 0) continue;

    hasActive = true;
    for (var v = 0; v < values.length; v++) {
      var label = (GROUP_LABELS[groupKey] || groupKey) + ': ' + values[v];
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'nco-filters__chip';
      chip.setAttribute('data-nco-chip', '');
      chip.setAttribute('data-nco-chip-group', groupKey);
      chip.setAttribute('data-nco-chip-value', values[v]);
      chip.setAttribute('aria-label', 'Remove filter: ' + label);
      chip.innerHTML = '<span class="nco-filters__chip-label">' + escapeHTML(label) + '</span>'
        + '<span class="nco-filters__chip-remove" data-nco-chip-remove aria-hidden="true">'
        + '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">'
        + '<line x1="2" y1="2" x2="8" y2="8" stroke="currentColor" stroke-width="1.5"/>'
        + '<line x1="8" y1="2" x2="2" y2="8" stroke="currentColor" stroke-width="1.5"/>'
        + '</svg></span>';

      if (clearBtn) {
        chipsContainer.insertBefore(chip, clearBtn);
      } else {
        chipsContainer.appendChild(chip);
      }
    }
  }

  if (clearBtn) {
    clearBtn.hidden = !hasActive;
  }
}

/* ------------------------------------------------------------------ */
/* URL state                                                           */
/* ------------------------------------------------------------------ */

function syncFiltersToURL() {
  var params = new URLSearchParams();
  for (var i = 0; i < URL_PARAM_ORDER.length; i++) {
    var urlKey = URL_PARAM_ORDER[i];
    var filterKey = URL_KEY_TO_FILTER[urlKey];
    if (!filterKey) continue;
    var values = activeFilters[filterKey];
    if (values && values.length > 0) {
      params.set(urlKey, values.join(','));
    }
  }
  var qs = params.toString();
  var url = window.location.pathname + (qs ? '?' + qs : '');
  history.replaceState(null, '', url);
}

function readFiltersFromURL() {
  var params = new URLSearchParams(window.location.search);
  var restored = {};
  params.forEach(function (value, key) {
    var filterKey = URL_KEY_TO_FILTER[key];
    if (filterKey) {
      restored[filterKey] = value.split(',').filter(Boolean);
    }
  });
  return restored;
}

/* ------------------------------------------------------------------ */
/* Filter state management                                             */
/* ------------------------------------------------------------------ */

function toggleFilter(groupKey, value) {
  if (!activeFilters[groupKey]) {
    activeFilters[groupKey] = [];
  }

  var idx = activeFilters[groupKey].indexOf(value);
  var protectedSelection = null;
  if (idx === -1) {
    if (SINGLE_SELECT_GROUPS[groupKey]) {
      activeFilters[groupKey] = [value];
    } else {
      activeFilters[groupKey].push(value);
    }
    protectedSelection = { groupKey: groupKey, value: value };
  } else {
    activeFilters[groupKey].splice(idx, 1);
  }

  pruneConflictingSelections(protectedSelection);
  applyFilters();
}

function cloneFilters(filters) {
  var copy = {};
  var keys = Object.keys(filters);
  for (var i = 0; i < keys.length; i++) {
    copy[keys[i]] = (filters[keys[i]] || []).slice();
  }
  return copy;
}

function canValueCoexistWithProtected(groupKey, value, protectedSelection) {
  if (!protectedSelection) return true;
  if (groupKey === protectedSelection.groupKey && value === protectedSelection.value) return true;

  var test = cloneFilters(activeFilters);
  test[protectedSelection.groupKey] = [protectedSelection.value];
  test[groupKey] = [value];

  return getMatchingProducts(test).length > 0;
}

function pruneConflictingSelections(protectedSelection) {
  if (protectedSelection) {
    var firstPassGroups = Object.keys(activeFilters);
    for (var pg = 0; pg < firstPassGroups.length; pg++) {
      var pgKey = firstPassGroups[pg];
      var pgValues = activeFilters[pgKey];
      if (!pgValues || pgValues.length === 0) continue;

      for (var pv = pgValues.length - 1; pv >= 0; pv--) {
        var pvValue = pgValues[pv];
        if (pgKey === protectedSelection.groupKey && pvValue === protectedSelection.value) continue;
        if (!canValueCoexistWithProtected(pgKey, pvValue, protectedSelection)) {
          pgValues.splice(pv, 1);
        }
      }
    }
  }

  var changed = true;
  var maxPasses = 5;
  while (changed && maxPasses-- > 0) {
    changed = false;
    var groupKeys = Object.keys(activeFilters);
    for (var g = 0; g < groupKeys.length; g++) {
      var groupKey = groupKeys[g];
      var values = activeFilters[groupKey];
      if (!values || values.length === 0) continue;

      var otherFilters = {};
      for (var f = 0; f < groupKeys.length; f++) {
        if (groupKeys[f] !== groupKey) {
          otherFilters[groupKeys[f]] = activeFilters[groupKeys[f]];
        }
      }

      for (var v = values.length - 1; v >= 0; v--) {
        if (
          protectedSelection &&
          groupKey === protectedSelection.groupKey &&
          values[v] === protectedSelection.value
        ) {
          continue;
        }
        var testFilter = {};
        testFilter[groupKey] = [values[v]];
        var combined = Object.assign({}, otherFilters, testFilter);
        var matchCount = getMatchingProducts(combined).length;

        if (matchCount === 0) {
          values.splice(v, 1);
          changed = true;
        }
      }
    }
  }
}

function removeFilter(groupKey, value) {
  if (!activeFilters[groupKey]) return;
  var idx = activeFilters[groupKey].indexOf(value);
  if (idx !== -1) {
    activeFilters[groupKey].splice(idx, 1);
  }
  updateOptionState(groupKey, value, false);
  applyFilters();
}

function clearAllFilters() {
  var groupKeys = Object.keys(activeFilters);
  for (var g = 0; g < groupKeys.length; g++) {
    var values = activeFilters[groupKeys[g]];
    for (var v = 0; v < values.length; v++) {
      updateOptionState(groupKeys[g], values[v], false);
    }
    activeFilters[groupKeys[g]] = [];
  }
  applyFilters();
}

function updateOptionState(groupKey, value, isActive) {
  var optionEl = document.querySelector(
    '[data-nco-filter-options="' + groupKey + '"] [data-nco-filter-value="' + CSS.escape(value) + '"]'
  );
  if (!optionEl) return;

  optionEl.classList.toggle(STATE.active, isActive);
  if (optionEl.tagName === 'BUTTON') {
    optionEl.setAttribute('aria-pressed', String(isActive));
  }
  var cbInput = optionEl.querySelector('.nco-filters__checkbox-input');
  if (cbInput) {
    cbInput.checked = isActive;
  }
}

function applyFilters() {
  var visibleProducts = getMatchingProducts(activeFilters);
  var visibleIds = visibleProducts.map(function (p) { return p.id; });
  var counts = calculateCounts();

  updateCountDisplay(counts);
  renderChips();
  updateAllActiveStates();
  syncFiltersToURL();

  // Build resolved variant data for each visible product
  var resolved = {};
  for (var i = 0; i < visibleProducts.length; i++) {
    var p = visibleProducts[i];
    resolved[p.id] = resolveFilteredVariant(p, activeFilters);
  }

  document.dispatchEvent(new CustomEvent('nco:filter:change', {
    detail: {
      visibleFamilyIds: visibleIds,
      activeFilters: activeFilters,
      totalProducts: products.length,
      visibleCount: visibleIds.length,
      resolved: resolved,
      products: products,
    },
  }));
}

function updateAllActiveStates() {
  var groupKeys = Object.keys(activeFilters);
  for (var g = 0; g < groupKeys.length; g++) {
    var groupKey = groupKeys[g];
    var groupOptions = allOptions[groupKey] || [];
    var activeValues = activeFilters[groupKey] || [];

    for (var o = 0; o < groupOptions.length; o++) {
      var value = groupOptions[o].value;
      var isActive = activeValues.indexOf(value) !== -1;
      updateOptionState(groupKey, value, isActive);
    }
  }
}

/* ------------------------------------------------------------------ */
/* Accordion toggle                                                    */
/* ------------------------------------------------------------------ */

function toggleAccordion(header) {
  var group = header.closest(SELECTORS.group);
  if (!group) return;

  var panel = group.querySelector('.nco-filters__group-panel');
  if (!panel) return;

  var expanded = header.getAttribute('aria-expanded') === 'true';
  header.setAttribute('aria-expanded', String(!expanded));
  group.classList.toggle(STATE.collapsed, expanded);
  panel.hidden = expanded;
}

/* ------------------------------------------------------------------ */
/* Event delegation                                                    */
/* ------------------------------------------------------------------ */

function handleContainerClick(e) {
  var header = e.target.closest(SELECTORS.groupHeader);
  if (header) {
    e.preventDefault();
    toggleAccordion(header);
    return;
  }

  var clearAll = e.target.closest(SELECTORS.clearAll);
  if (clearAll) {
    e.preventDefault();
    clearAllFilters();
    return;
  }

  var chipEl = e.target.closest(SELECTORS.chip);
  if (chipEl) {
    e.preventDefault();
    var chipGroup = chipEl.getAttribute('data-nco-chip-group');
    var chipValue = chipEl.getAttribute('data-nco-chip-value');
    if (chipGroup && chipValue) {
      removeFilter(chipGroup, chipValue);
    }
    return;
  }

  var optionEl = e.target.closest(SELECTORS.option);
  if (optionEl) {
    if (optionEl.classList.contains(STATE.disabled)) {
      e.preventDefault();
      return;
    }

    if (optionEl.tagName === 'LABEL') return;

    var groupEl = optionEl.closest(SELECTORS.group);
    if (!groupEl) return;

    var groupKey = groupEl.getAttribute('data-nco-filter-group');
    var value = optionEl.getAttribute('data-nco-filter-value');
    if (!groupKey || !value) return;

    e.preventDefault();
    toggleFilter(groupKey, value);
    return;
  }
}

function handleCheckboxChange(e) {
  var cbInput = e.target;
  if (!cbInput.classList.contains('nco-filters__checkbox-input')) return;

  var optionEl = cbInput.closest(SELECTORS.option);
  if (!optionEl) return;

  var groupEl = optionEl.closest(SELECTORS.group);
  if (!groupEl) return;

  var groupKey = groupEl.getAttribute('data-nco-filter-group');
  var value = optionEl.getAttribute('data-nco-filter-value');
  if (!groupKey || !value) return;

  toggleFilter(groupKey, value);
}

/* ------------------------------------------------------------------ */
/* Init                                                                */
/* ------------------------------------------------------------------ */

function init() {
  var indexEl = document.querySelector(SELECTORS.index);
  if (!indexEl) return;

  try {
    products = JSON.parse(indexEl.textContent);
  } catch (err) {
    return;
  }

  if (!Array.isArray(products) || products.length === 0) return;

  var container = document.querySelector(SELECTORS.container);
  if (!container) return;

  buildAllOptions();

  var groupKeys = Object.keys(allOptions);
  for (var i = 0; i < groupKeys.length; i++) {
    activeFilters[groupKeys[i]] = [];
  }

  renderAllOptions();

  // Restore filters from URL params
  var urlFilters = readFiltersFromURL();
  var urlKeys = Object.keys(urlFilters);
  for (var u = 0; u < urlKeys.length; u++) {
    var fk = urlKeys[u];
    if (activeFilters.hasOwnProperty(fk)) {
      activeFilters[fk] = urlFilters[fk];
    }
  }

  var counts = calculateCounts();
  updateCountDisplay(counts);
  updateAllActiveStates();

  container.addEventListener('click', handleContainerClick);
  container.addEventListener('change', handleCheckboxChange);

  // Initial filter event
  var initVisible = getMatchingProducts(activeFilters);
  var initResolved = {};
  for (var r = 0; r < initVisible.length; r++) {
    initResolved[initVisible[r].id] = resolveFilteredVariant(initVisible[r], activeFilters);
  }

  document.dispatchEvent(new CustomEvent('nco:filter:change', {
    detail: {
      visibleFamilyIds: initVisible.map(function (p) { return p.id; }),
      activeFilters: activeFilters,
      totalProducts: products.length,
      visibleCount: initVisible.length,
      resolved: initResolved,
      products: products,
    },
  }));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
